import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Check, FileImage, Loader2, MessageCircle, Upload } from "lucide-react";
import { API, CATEGORIES, PRICE_CATEGORIES, scrollToId, waLink } from "@/config";

const EASE = [0.16, 1, 0.3, 1];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EnquirySection = ({ category, setCategory }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [details, setDetails] = useState("");
  const [price, setPrice] = useState("");
  const [shot, setShot] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(null);

  const showPrice = PRICE_CATEGORIES.includes(category);

  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please attach an image (JPG, PNG or WEBP)");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Keep the screenshot under 8 MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/upload`, fd);
      setShot(data);
      toast.success("Screenshot attached");
    } catch (err) {
      toast.error((err.response && err.response.data && err.response.data.detail) || "Upload failed — try again");
    } finally {
      setUploading(false);
    }
  };

  const buildMessage = (q) =>
    [
      "Hi! I want a better price on my trip.",
      "",
      `Name: ${q.name}`,
      `Category: ${q.category}`,
      q.details && `Trip: ${q.details}`,
      q.quoted_price ? `Price I'm getting: ₹${q.quoted_price.toLocaleString("en-IN")}` : "",
      q.screenshot_url ? "Screenshot: attached" : ""
    ]
      .filter(Boolean)
      .join("\n");

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const digits = phone.replace(/\D/g, "");
    if (name.trim().length < 2) {
      toast.error("Please share your name");
      return;
    }
    if (digits.length < 10) {
      toast.error("Please enter a valid WhatsApp number");
      return;
    }
    const payload = {
      name: name.trim(),
      phone: digits,
      category,
      details: details.trim(),
      quoted_price: price ? Number(price) : null,
      screenshot_url: shot ? shot.url : null
    };
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/enquiries`, payload);
      setSaved(data);
      toast.success("Request saved — one tap to send it on WhatsApp");
      window.open(waLink(buildMessage(data)), "_blank");
    } catch (err) {
      toast.error((err.response && err.response.data && err.response.data.detail) || "Couldn't save your request — try again");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSaved(null);
    setName("");
    setPhone("");
    setDetails("");
    setPrice("");
    setShot(null);
  };

  return (
    <section id="get-started" className="py-20 lg:py-32" data-testid="enquiry-section">
      <div className="container-edit grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5 lg:sticky lg:top-28 lg:self-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, ease: EASE }}
          >
            <p className="eyebrow mb-5 flex items-center gap-4">
              <span className="inline-block h-px w-10 bg-gold" /> Get a better price
            </p>
            <h2 className="font-serif text-3xl font-medium leading-tight tracking-tight text-ink sm:text-4xl">
              Tell us the number <em className="italic text-ocean">you were quoted.</em>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-ink2">
              Fill this in once. Your request lands with our concierge, and your WhatsApp
              opens with the details already typed — press send and we start working.
            </p>
            <div className="mt-8 border-t border-ink/15 pt-8">
              <button
                data-testid="whatsapp-direct-button"
                onClick={() => window.open(waLink("Hi! I want a better price on my trip."), "_blank")}
                className="inline-flex items-center gap-3 rounded-full bg-wa px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:brightness-110 active:scale-[0.98]"
              >
                <MessageCircle className="h-4 w-4" />
                Prefer to just chat? WhatsApp us
              </button>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
                The screenshot & price are optional — but they get you a sharper number, faster.
              </p>
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-7">
          <motion.div
            className="rounded-xl border border-ink/20 bg-white p-6 shadow-[0_24px_60px_-40px_rgba(17,19,21,0.35)] sm:p-10"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
          >
            {saved ? (
              <div className="py-8 text-center" data-testid="enquiry-success">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-ocean text-ivory">
                  <Check className="h-6 w-6" />
                </span>
                <h3 className="mt-6 font-serif text-3xl font-medium text-ink">Request saved.</h3>
                <p className="mx-auto mt-3 max-w-sm text-[15px] leading-relaxed text-ink2">
                  It's with our concierge. One tap to send it straight to us on WhatsApp —
                  your details are already typed in.
                </p>
                <button
                  data-testid="whatsapp-continue-button"
                  onClick={() => window.open(waLink(buildMessage(saved)), "_blank")}
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-wa px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:brightness-110 active:scale-[0.98]"
                >
                  <MessageCircle className="h-4 w-4" />
                  Send on WhatsApp
                </button>
                <button
                  data-testid="enquiry-reset-button"
                  onClick={reset}
                  className="mt-6 block w-full font-mono text-[11px] uppercase tracking-[0.2em] text-smoke transition-colors hover:text-ink"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="enquiry-form" className="space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="field-label" htmlFor="enq-name">Full name</label>
                    <input
                      id="enq-name"
                      data-testid="enquiry-name"
                      className="field"
                      placeholder="Aarav Sharma"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="enq-phone">WhatsApp number</label>
                    <div className="flex items-center gap-2 rounded-xl border border-ink/15 bg-white px-4 focus-within:border-ocean focus-within:ring-2 focus-within:ring-ocean/15">
                      <span className="font-mono text-sm text-smoke">+91</span>
                      <input
                        id="enq-phone"
                        data-testid="enquiry-phone"
                        className="w-full bg-transparent py-3 text-[15px] outline-none placeholder:text-stone-400"
                        placeholder="98765 43210"
                        inputMode="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="field-label" htmlFor="enq-category">What are you booking?</label>
                  <select
                    id="enq-category"
                    data-testid="enquiry-category"
                    className="field appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%237A7E7B%22 stroke-width=%222%22%3E%3Cpath d=%22m6 9 6 6 6-6%22/%3E%3C/svg%3E')] bg-[position:right_1rem_center] bg-no-repeat pr-10"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="field-label" htmlFor="enq-details">Trip details</label>
                  <textarea
                    id="enq-details"
                    data-testid="enquiry-details"
                    className="field min-h-[96px] resize-y"
                    placeholder="DEL → GOA, 12 Mar · or hotel name + check-in dates"
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                  />
                </div>

                {showPrice && (
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2" data-testid="price-upload-group">
                    <div>
                      <label className="field-label" htmlFor="enq-price">Price you're getting (₹)</label>
                      <input
                        id="enq-price"
                        data-testid="quoted-price-input"
                        className="field font-mono"
                        placeholder="6,400"
                        inputMode="numeric"
                        value={price}
                        onChange={(e) => setPrice(e.target.value.replace(/[^\d]/g, ""))}
                      />
                    </div>
                    <div>
                      <span className="field-label">Screenshot of your quote</span>
                      <label
                        data-testid="screenshot-input"
                        className="flex h-[46px] cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-ink/30 bg-ivory2 px-4 text-[13px] font-medium text-ink2 transition-colors hover:border-ocean hover:text-ocean"
                      >
                        {uploading ? (
                          <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</>
                        ) : shot ? (
                          <><FileImage className="h-4 w-4 text-ocean" /> Attached — replace</>
                        ) : (
                          <><Upload className="h-4 w-4" /> Upload screenshot</>
                        )}
                        <input type="file" accept="image/*" className="hidden" onChange={onFile} />
                      </label>
                    </div>
                  </div>
                )}

                {shot && (
                  <div className="flex items-center gap-4 rounded-xl border border-ink/10 bg-ivory2 p-3" data-testid="screenshot-preview">
                    <img src={`${BACKEND_URL}${shot.url}`} alt="Your quote screenshot" className="h-16 w-16 rounded-lg border border-ink/10 object-cover" />
                    <div className="text-[13px] text-ink2">
                      <p className="font-semibold text-ink">Screenshot attached</p>
                      <p className="font-mono text-[11px]">We'll read every rupee on it</p>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  data-testid="enquiry-submit-button"
                  disabled={submitting}
                  className="btn-ocean w-full disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {submitting ? "Saving your request…" : "Get my better price"}
                  {!submitting && <ArrowUpRight className="h-4 w-4" />}
                </button>
                <p className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
                  Saved to our desk · then WhatsApp opens with your details typed in
                </p>
              </form>
            )}
          </motion.div>

          <button
            data-testid="scroll-to-categories-button"
            onClick={() => scrollToId("categories")}
            className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-smoke transition-colors hover:text-ink"
          >
            ← Browse the five categories again
          </button>
        </div>
      </div>
    </section>
  );
};

export default EnquirySection;
